import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShieldAlert, XCircle, MessageCircle, ExternalLink, Globe, AlertTriangle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VideoPlayer from '../components/VideoPlayer';
import TestettiKVideoPlayer from '../components/TestettiKVideoPlayer';
import { canRenderPublicVideo } from '../utils/videoUrlValidation';
import ScoreHistoryTimeline from '../components/ScoreHistoryTimeline';
import TestProcedureCard from '../components/TestProcedureCard';
import { trackPageView } from '../utils/analytics';

import { API_URL } from '../utils/apiUrl';

const OG_FALLBACK_IMAGE = 'https://www.testettik.com/og-default.png';
const SITE_DOMAIN = 'https://www.testettik.com';

const formatPriceTR = (price) => {
  if (price == null) return null;
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(price);
};

// TT-PRODUCT-IMAGE-UPLOAD-001 / TT-PRODUCT-IMAGE-GALLERY-001: visible product image + gallery.
// Accepts both external HTTPS URLs and GridFS-served /api/media/product-images/{id}.
// If product has a gallery (product.images), shows thumbnail row below the main image.
const ProductImageBlock = ({ imageUrl, images, name }) => {
  const gallery = Array.isArray(images) ? images.filter(Boolean).slice(0, 5) : [];
  const primary = gallery[0] || imageUrl;
  const [activeIdx, setActiveIdx] = React.useState(0);
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => { setActiveIdx(0); setBroken(false); }, [primary]);
  if (!primary) return null;
  const currentUrl = gallery.length > 0 ? gallery[activeIdx] || primary : primary;
  const isValid = /^https?:\/\//i.test(currentUrl) || currentUrl.startsWith('/api/media/product-images/');
  if (!isValid || broken) return null;
  return (
    <div className="mb-8 border border-neutral-200 rounded-md bg-white p-4 shadow-sm" data-testid="product-image-block">
      <div className="w-full flex items-center justify-center bg-neutral-50 rounded overflow-hidden" style={{ minHeight: 240 }}>
        <img
          src={currentUrl}
          alt={name ? `${name} ürün görseli` : 'Ürün görseli'}
          className="max-h-[420px] w-auto object-contain"
          loading="lazy"
          onError={() => setBroken(true)}
          data-testid="product-detail-image"
        />
      </div>
      {gallery.length > 1 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="product-detail-gallery-thumbs">
          {gallery.map((u, i) => (
            <button
              key={`${u}-${i}`}
              type="button"
              onClick={() => setActiveIdx(i)}
              className={`w-14 h-14 rounded border overflow-hidden bg-neutral-50 transition ${i === activeIdx ? 'border-emerald-500 ring-1 ring-emerald-400' : 'border-neutral-200 hover:border-neutral-400'}`}
              data-testid={`product-detail-thumb-${i}`}
              aria-label={`Görsel ${i + 1}`}
            >
              <img src={u} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const setOGMeta = (product) => {
  if (!product) return;
  const title = `${product.name || 'Ürün'} Test Sonucu — ${product.score?.toFixed(1) || '?'}/10`.slice(0, 60);
  const desc = (product.description || 'Bu ürün TestettiK® tarafından test edilmiştir.').slice(0, 150);
  const image = product.image_url || OG_FALLBACK_IMAGE;
  const url = `${SITE_DOMAIN}/products/${product.id || ''}`;

  document.title = `${product.name || 'Ürün'} Test Sonucu | TestettiK®`;

  const setMeta = (prop, content) => {
    let el = document.querySelector(`meta[property="${prop}"]`) || document.querySelector(`meta[name="${prop}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(prop.startsWith('og:') ? 'property' : 'name', prop);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  setMeta('og:title', title);
  setMeta('og:description', desc);
  setMeta('og:image', image.startsWith('http') ? image : OG_FALLBACK_IMAGE);
  setMeta('og:url', url);
  setMeta('og:type', 'product');
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', title);
  setMeta('twitter:description', desc);
  setMeta('twitter:image', image.startsWith('http') ? image : OG_FALLBACK_IMAGE);
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const viewTracked = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/products/${id}`);
        setProduct(data);
        setOGMeta(data);
        trackPageView('product_detail_view', { productId: id, sellerId: data.seller_id });
        // Track view via verification tracking endpoint (once per mount)
        if (!viewTracked.current) {
          viewTracked.current = true;
          const payload = JSON.stringify({ product_id: id, source: 'product_page' });
          if (navigator.sendBeacon) {
            navigator.sendBeacon(`${API_URL}/api/track/verification-view`, new Blob([payload], { type: 'application/json' }));
          } else {
            fetch(`${API_URL}/api/track/verification-view`, { method: 'POST', body: payload, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => {});
          }
        }
      } catch (e) {
        // TT-P0-PUBLIC-VISIBILITY-001: distinguish 404 "not-yet-public" from other errors
        const detail = e?.response?.data?.detail;
        if (detail === 'Bu ürün henüz yayında değil.') {
          setError('Bu ürün henüz yayında değil.');
        } else {
          setError('Ürün bulunamadı.');
        }
      }
      setLoading(false);
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-32">
          <div className="text-lg font-bold text-neutral-400">Yükleniyor...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="text-lg font-bold mb-4 text-neutral-700">{error}</div>
            <Link to="/" className="text-sm font-semibold text-[#1C3B2A] hover:underline">Ana Sayfaya Dön</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isApproved = product.score >= 7;
  const isLowScore = product.score < 7;
  const sellerAction = product.seller_action;
  const hasSellerAction = sellerAction && sellerAction.type;

  const handleSellerClick = (e, action) => {
    try {
      const payload = JSON.stringify({ product_id: id, source: 'product_page' });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API_URL}/api/track/seller-click`, new Blob([payload], { type: 'application/json' }));
      } else {
        fetch(`${API_URL}/api/track/seller-click`, { method: 'POST', body: payload, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => {});
      }
    } catch {}
    if (action.type === 'whatsapp') {
      window.location.href = action.url;
    } else {
      window.open(action.url, '_blank', 'noopener,noreferrer');
    }
    e.preventDefault();
  };

  const getSellerButtonIcon = (type) => {
    if (type === 'whatsapp') return <MessageCircle className="w-5 h-5" />;
    if (type === 'instagram') return <ExternalLink className="w-5 h-5" />;
    return <Globe className="w-5 h-5" />;
  };

  const getSellerButtonColor = (type) => {
    if (type === 'whatsapp') return 'bg-[#25D366] hover:bg-[#1fb855]';
    if (type === 'instagram') return 'bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90';
    return 'bg-[#1C3B2A] hover:bg-[#15312a]';
  };

  // Badge expiration only applies to approved products
  const isBadgeExpired = isApproved && (
    product.badge_expired === true ||
    (!product.badge_active && product.badge_expires_at)
  );

  // INACTIVE = approved but badge expired/inactive
  const isInactive = isApproved && isBadgeExpired;

  // STATE: Low score — failure
  if (isLowScore) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <Link to="/products" className="flex items-center gap-2 font-semibold text-sm text-neutral-500 hover:text-neutral-900 transition mb-8" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Ürünlere Dön
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {product.video_url && (
                <VideoPlayer url={product.video_url} videoType={product.video_type} originalUrl={product.video_url} title={`${product.name} test videosu`} />
              )}
              <ProductImageBlock imageUrl={product.image_url} images={product.images} name={product.name} />
              <div className="border border-neutral-200 rounded-md p-8 md:p-12 bg-white text-center mb-8 shadow-sm">
                <div className="inline-block px-8 py-5 rounded-md mb-6 bg-[#7A1F1F]">
                  <span className="text-6xl md:text-7xl font-mono font-black text-white">{product.score?.toFixed(1)}</span>
                  <span className="text-xl md:text-2xl font-mono font-bold text-white/80"> / 10</span>
                </div>
                <div className="mb-6">
                  <span className="inline-block px-6 py-3 rounded-md text-lg md:text-xl font-black text-white bg-[#7A1F1F]" data-testid="verdict-fail">
                    Beklentiyi Karşılamadı
                  </span>
                </div>
                <p className="text-base text-neutral-600 max-w-2xl mx-auto leading-relaxed">{product.description}</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border border-neutral-200 rounded-md bg-white p-6 shadow-sm">
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase text-neutral-400 mb-1">ÜRÜN ADI</p>
                  <h2 className="text-xl font-black" data-testid="product-name">{product.name}</h2>
                </div>
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase text-neutral-400 mb-1">MARKA</p>
                  <p className="text-lg font-bold" data-testid="product-brand">{product.brand}</p>
                </div>
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase text-neutral-400 mb-1">KATEGORİ</p>
                  <p className="text-base font-semibold text-neutral-600">{product.category}</p>
                </div>
                <div className="border-t border-neutral-100 pt-4 mt-4">
                  <div className="flex items-center gap-2 text-[#7A1F1F]">
                    <XCircle className="w-5 h-5" strokeWidth={2} />
                    <span className="text-sm font-bold" data-testid="fail-status">Bu ürün testi geçemedi</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // STATE: INACTIVE — approved but badge expired
  if (isInactive) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <Link to="/products" className="flex items-center gap-2 font-semibold text-sm text-neutral-500 hover:text-neutral-900 transition mb-8" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Ürünlere Dön
          </Link>

          {/* WARNING BLOCK — high visibility */}
          <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-6 md:p-8 mb-8" data-testid="inactive-warning">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-xl md:text-2xl font-black text-amber-800 mb-2" data-testid="inactive-title">
                  Bu ürünün doğrulama süresi sona ermiştir.
                </h2>
                <p className="text-base text-amber-700 font-medium mb-3">
                  Bu ürün için şu anda aktif bir doğrulama bulunmamaktadır.
                </p>
                <p className="text-sm text-amber-600">
                  Bu sayfadaki bilgiler yalnızca geçmiş test sonucunu göstermektedir.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {product.video_url && (
                <VideoPlayer url={product.video_url} videoType={product.video_type} originalUrl={product.video_url} title={`${product.name} test videosu`} />
              )}
              <ProductImageBlock imageUrl={product.image_url} images={product.images} name={product.name} />
              <div className="border border-neutral-200 rounded-md p-8 md:p-12 bg-white text-center mb-8 shadow-sm">
                <p className="text-xs font-bold uppercase text-neutral-400 mb-4">Geçmiş Test Skoru</p>
                <div className="inline-block px-8 py-5 rounded-md mb-6 bg-neutral-400">
                  <span className="text-6xl md:text-7xl font-mono font-black text-white">{product.score?.toFixed(1)}</span>
                  <span className="text-xl md:text-2xl font-mono font-bold text-white/80"> / 10</span>
                </div>
                {/* NO "Test Başarılı" badge, NO seller action button */}
                <p className="text-base text-neutral-600 max-w-2xl mx-auto leading-relaxed mt-4">{product.description}</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="border border-neutral-200 rounded-md bg-white p-6 shadow-sm">
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase text-neutral-400 mb-1">ÜRÜN ADI</p>
                  <h2 className="text-xl font-black" data-testid="product-name">{product.name}</h2>
                </div>
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase text-neutral-400 mb-1">MARKA</p>
                  <p className="text-lg font-bold" data-testid="product-brand">{product.brand}</p>
                </div>
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase text-neutral-400 mb-1">KATEGORİ</p>
                  <p className="text-base font-semibold text-neutral-600">{product.category}</p>
                </div>
                <div className="border-t border-neutral-100 pt-4 mt-4">
                  <div className="flex items-center gap-2 text-amber-600">
                    <ShieldAlert className="w-5 h-5" strokeWidth={2} />
                    <span className="text-sm font-bold" data-testid="inactive-status">Doğrulama süresi doldu</span>
                  </div>
                </div>
              </div>

              {/* Recovery CTA */}
              <Link to="/submit"
                className="block w-full text-center bg-neutral-100 border border-neutral-200 rounded-md p-4 text-sm font-semibold text-[#1C3B2A] hover:bg-neutral-200 transition"
                data-testid="recovery-cta">
                Güncel doğrulama talep edin
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // STATE: Approved (score >= 7) AND badge active — full content
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Link to="/products" className="flex items-center gap-2 font-semibold text-sm text-neutral-500 hover:text-neutral-900 transition mb-8" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Ürünlere Dön
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {canRenderPublicVideo(product.video) ? (
              <div className="mb-8" data-testid="testettik-video-section">
                <p className="text-[10px] font-black uppercase tracking-widest mb-2 text-center" style={{ color: 'var(--text-muted)' }}>TestettiK Video Kanıtı</p>
                <TestettiKVideoPlayer video={product.video} title={`${product.name} — TestettiK Video Kanıtı`} />
                <p className="text-xs text-center mt-3 font-medium" style={{ color: 'var(--text-secondary-strong)' }}>
                  Bu video, ürünün TestettiK bağımsız test süreci kapsamında hazırlanmıştır.
                </p>
              </div>
            ) : product.video_url && (
              <VideoPlayer url={product.video_url} videoType={product.video_type} originalUrl={product.video_url} title={`${product.name} test videosu`} />
            )}
            <ProductImageBlock imageUrl={product.image_url} images={product.images} name={product.name} />
            <div className="border border-neutral-200 rounded-md p-8 md:p-12 bg-white text-center mb-8 shadow-sm">
              <div className="inline-block px-8 py-5 rounded-md mb-6" style={{ backgroundColor: product.score_color || '#1C3B2A' }}>
                <span className="text-6xl md:text-7xl font-mono font-black text-white">{product.score?.toFixed(1)}</span>
                <span className="text-xl md:text-2xl font-mono font-bold text-white/80"> / 10</span>
              </div>
              <div className="mb-6">
                <span className="inline-block px-6 py-3 rounded-md text-lg md:text-xl font-black text-white" style={{ backgroundColor: product.score_color || '#1C3B2A' }} data-testid="verdict-approved">
                  Test Onaylandı – Ürün Doğrulandı
                </span>
              </div>

              {hasSellerAction ? (
                <a href={sellerAction.url} onClick={(e) => handleSellerClick(e, sellerAction)}
                  className={`flex items-center justify-center gap-2.5 w-full py-4 text-white font-bold rounded-lg transition text-base mb-6 ${getSellerButtonColor(sellerAction.type)}`}
                  data-testid="seller-action-button">
                  {getSellerButtonIcon(sellerAction.type)}
                  {sellerAction.label}
                </a>
              ) : (
                <p className="text-center text-sm text-neutral-500 bg-neutral-50 rounded-lg py-4 px-4 border border-neutral-200 mb-6" data-testid="seller-action-fallback">
                  Bu ürün TestettiK® tarafından onaylanmıştır. Satıcı ile doğrudan iletişime geçerek sipariş verebilirsiniz.
                </p>
              )}

              {hasSellerAction && (
                <p className="text-xs text-neutral-400 text-center -mt-4 mb-4">Satın alma işlemi için satıcıya yönlendirilirsiniz.</p>
              )}

              {/* Price Block */}
              {product.price != null && product.price > 0 && (
                <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mb-6 text-center" data-testid="price-block">
                  <p className="text-xs font-bold uppercase text-neutral-400 mb-1">Test sırasında görülen satış fiyatı</p>
                  <p className="text-2xl font-black text-[#111]" data-testid="price-value">{formatPriceTR(product.price)}</p>
                  <p className="text-[10px] text-neutral-400 mt-1">Fiyat satıcı tarafından değiştirilebilir.</p>
                  {product.price_updated_at && (
                    <p className="text-[10px] text-neutral-400">Son güncelleme: {product.price_updated_at.split('T')[0]}</p>
                  )}
                  <p className="text-[10px] text-neutral-500 mt-2 italic">Fiyat-performans değerlendirmesi toplam puana dahildir.</p>
                </div>
              )}

              <p className="text-base text-neutral-600 max-w-2xl mx-auto leading-relaxed" data-testid="active-description">{product.description}</p>
            </div>

            {/* ── Test Procedures (category-based) ── */}
            <TestProcedureCard category={product.category} />

            {/* ── Güvenİzi (Trust Trace) Timeline ── */}
            <ScoreHistoryTimeline productId={product.id} />
          </div>

          <div className="space-y-6">
            <div className="border border-neutral-200 rounded-md bg-white p-6 shadow-sm">
              <div className="mb-5">
                <p className="text-xs font-bold uppercase text-neutral-400 mb-1">ÜRÜN ADI</p>
                <h2 className="text-xl font-black" data-testid="product-name">{product.name}</h2>
              </div>
              <div className="mb-5">
                <p className="text-xs font-bold uppercase text-neutral-400 mb-1">MARKA</p>
                <p className="text-lg font-bold" data-testid="product-brand">{product.brand}</p>
              </div>
              <div className="mb-5">
                <p className="text-xs font-bold uppercase text-neutral-400 mb-1">KATEGORİ</p>
                <p className="text-base font-semibold text-neutral-600">{product.category}</p>
              </div>
              {product.badge_code && (
                <div className="border-t border-neutral-100 pt-4 mt-4 space-y-3">
                  <div>
                    <p className="text-xs font-bold uppercase text-neutral-400 mb-1">TestettiK® GÜVEN ROZETİ</p>
                    <p className="text-sm font-mono font-bold text-[#1C3B2A]" data-testid="badge-code">{product.badge_code}</p>
                  </div>
                  {product.test_date && (
                    <div>
                      <p className="text-xs font-bold uppercase text-neutral-400 mb-1">TEST TARİHİ</p>
                      <p className="text-sm font-semibold" data-testid="test-date">{product.test_date}</p>
                    </div>
                  )}
                  {product.badge_expires_at && (
                    <div>
                      <p className="text-xs font-bold uppercase text-neutral-400 mb-1">DOĞRULAMA GEÇERLİLİK TARİHİ</p>
                      <p className="text-sm font-semibold" data-testid="expiry-date">{product.badge_expires_at.split('T')[0]}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
